import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Select from "react-select";
import countryList from "react-select-country-list";
import { State } from "country-state-city";
import { useAuth } from '../contexts/AuthContext';
import { useProfileInfo } from '../contexts/ProfileInfoContext';
import { useProfileViews } from '../hooks/useProfileViews';
import { supabase } from '../supabase';
import useSnackbarNotification from '../hooks/useSnackbarNotification';

// Map a role category to a CSS class so colors can be controlled centrally
const getCategoryClass = (category) => {
    if (!category) return '';
    const categoryMap = {
        'Business/Consulting': 'business-consulting',
        'Clinical Medicine': 'clinical-medicine',
        'Clinical Research': 'clinical-research',
        'Entrepreneurship': 'entrepreneurship',
        'HealthTech': 'health-tech',
        'Hospital/Clinic Management': 'hospital-clinic-management',
        'Insurance': 'insurance',
        'Marketing/Sales': 'marketing-sales',
        'Medical Communication': 'medical-communication',
        'Medical Education': 'medical-education',
        'Pharmaceuticals': 'pharmaceuticals',
        'Policy/Legal/Ethics': 'policy-legal-ethics',
        'Project Management': 'project-management',
        'Wellness/lifestyle': 'wellness-lifestyle',
    };
    return categoryMap[category] || '';
};

const ROLE_CATEGORY_OPTIONS = [
    'Business/Consulting',
    'Clinical Medicine',
    'Clinical Research',
    'Entrepreneurship',
    'HealthTech',
    'Hospital/Clinic Management',
    'Insurance',
    'Marketing/Sales',
    'Medical Communication',
    'Medical Education',
    'Pharmaceuticals',
    'Policy/Legal/Ethics',
    'Project Management',
    'Wellness/lifestyle',
];

const EMPLOYMENT_TYPE_OPTIONS = ['Full-time', 'Part-time', 'Locum', 'Internship', 'Remote only'];

const employmentTypeOptions = [
    { value: "full-time", label: "Full-time" },
    { value: "part-time", label: "Part-time" },
    { value: "contract", label: "Contract" }
];

const locationTypeOptions = [
    { value: "on-site", label: "On-site" },
    { value: "remote", label: "Remote" },
    { value: "hybrid", label: "Hybrid" }
];

// Helper function to format date as "Oct 2024"
const formatDateMonthYear = (dateString) => {
    if (!dateString) return '';
    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString;
        const month = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][date.getMonth()];
        const year = date.getFullYear();
        return `${month} ${year}`;
    } catch (e) {
        return dateString;
    }
};

// Helper function to calculate duration between two dates
const calculateDuration = (startDate, endDate, isCurrent) => {
    if (!startDate) return '';
    try {
        const start = new Date(startDate);
        const end = isCurrent ? new Date() : (endDate ? new Date(endDate) : new Date());
        if (isNaN(start.getTime()) || isNaN(end.getTime())) return '';
        
        const years = end.getFullYear() - start.getFullYear();
        const months = end.getMonth() - start.getMonth();
        let totalMonths = years * 12 + months;
        if (end.getDate() < start.getDate()) totalMonths--;
        
        const yearsPart = Math.floor(totalMonths / 12);
        const monthsPart = totalMonths % 12;
        
        let duration = '';
        if (yearsPart > 0) {
            duration += `${yearsPart} ${yearsPart === 1 ? 'yr' : 'yrs'}`;
        }
        if (monthsPart > 0) {
            if (duration) duration += ' ';
            duration += `${monthsPart} ${monthsPart === 1 ? 'mo' : 'mos'}`;
        }
        if (!duration) {
            duration = '< 1 mo';
        }
        return duration;
    } catch (e) {
        return '';
    }
};

// Helper function to format employment type for display
const formatEmploymentType = (employmentType) => {
    if (!employmentType) return '';
    // Handle different formats: "full-time", "Full-time", "Full-Time", etc.
    // Convert from value format (e.g., "full-time") to display format (e.g., "Full-Time")
    if (employmentType.includes('-')) {
        return employmentType
            .split('-')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join('-');
    }
    // If no hyphen, just capitalize first letter
    return employmentType.charAt(0).toUpperCase() + employmentType.slice(1).toLowerCase();
};

const createEmptyExperience = () => ({
    company: '',
    title: '',
    isCurrent: false,
    startDate: '',
    endDate: '',
    description: '',
    country: '',
    countryCode: '',
    provinceState: '',
    employmentType: '',
    locationType: '',
});

const createEmptyEducation = () => ({
    school: '',
    fieldOfStudy: '',
    degree: '',
    grade: '',
    isCurrent: false,
    startDate: '',
    endDate: '',
    description: '',
    country: '',
    countryCode: '',
    provinceState: '',
});

const createEmptyCertification = () => ({
    courseName: '',
    issuingOrg: '',
    issueDate: '',
    expiryDate: '',
    credentialId: '',
    credentialUrl: '',
});

const MyProfile = () => {
    const { user } = useAuth();
    const { profileInfo, getProfileInfo } = useProfileInfo();
    const { trackProfileView } = useProfileViews();
    const { showSuccess, showError } = useSnackbarNotification();
    const [isEditing, setIsEditing] = useState({});
    const [isEditOpen, setIsEditOpen] = useState(false);
    const [activeSection, setActiveSection] = useState(null);
    const [profileImageUploading, setProfileImageUploading] = useState(false);
    const fileInputRef = useRef(null);
    const [editForm, setEditForm] = useState({
        // profile
        firstName: '',
        lastName: '',
        headline: '',
        country: '',
        countryCode: '',
        provinceState: '',
        linkedinUrl: '',
        profileVisibility: '',
        // about
        aboutText: '',
        // skills
        skills: [],
        languages: [],
        // roles open to
        rolesOpenTo: [],
        roleTitles: [],
        employmentTypes: [],
        _roleInput: '',
        // experience & education
        experiences: [],
        educations: [],
        // certifications
        certifications: [],
    });
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    
    // Check if this profile is being viewed by an employer
    const isEmployerView = searchParams.get('viewer') === 'employer';
    const candidateId = searchParams.get('candidateId');

    useEffect(() => {
        if (isEmployerView && candidateId && user?.id !== candidateId) {
            // Track profile view when viewed by someone else
            const context = {
                source: 'employer_candidate_view',
                profileRole: 'Employee',
                referrer: document.referrer,
                userAgent: navigator.userAgent
            };
            
            trackProfileView(candidateId, context);
        }
    }, [isEmployerView, candidateId, user, trackProfileView]);

    // Get profile data from users_profiles table (same as employer candidate view)
    const getUserData = () => {
        // Use profileInfo from users_profiles table for consistency with employer candidate view
        const profile = profileInfo || {};
        
        return {
            // Basic info
            fullName: profile.firstname && profile.lastname 
                ? `${profile.firstname} ${profile.lastname}` 
                : profile.firstname || profile.lastname || 'User',
            headline: profile.headline || '',
            email: user?.email || profile.email || '',
            location: profile.location_city_country || '',
            linkedinUrl: profile.linkedinurl || '',
            profileImageUrl: profile.profile_image_url || '',
            hpcsaRegNo: (profile.hpcsa_reg_no || '').trim(),
            hpcsaVerified: (() => {
                const value = profile.hpcsa_verified;
                return value === true || value === 'true' || value === 1 || value === '1' || value === 't' || value === 'T';
            })(),
            
            // About text
            aboutText: profile.profile_about || '',
            
            // Profile visibility
            profileVisibility: profile.profile_visibility || 'public',
            
            // Skills (parse JSON if stored as string)
            skills: profile.skills ? (typeof profile.skills === 'string' ? JSON.parse(profile.skills) : profile.skills) : [],
            
            // Work experiences (parse JSON if stored as string)
            workExperiences: profile.work_experiences ? (typeof profile.work_experiences === 'string' ? JSON.parse(profile.work_experiences) : profile.work_experiences) : [],
            
            // Education (parse JSON if stored as string)
            educations: profile.educations ? (typeof profile.educations === 'string' ? JSON.parse(profile.educations) : profile.educations) : [],
            
            // Certifications (parse JSON if stored as string)
            certifications: profile.certifications ? (typeof profile.certifications === 'string' ? JSON.parse(profile.certifications) : profile.certifications) : [],
            
            // Languages (parse JSON if stored as string)
            languages: profile.languages ? (typeof profile.languages === 'string' ? JSON.parse(profile.languages) : profile.languages) : [],
            
            // Roles open to - captured during onboarding step 1
            rolesOpenTo: profile.selected_categories ? (typeof profile.selected_categories === 'string' ? JSON.parse(profile.selected_categories) : profile.selected_categories) : [],
            roleTitles: profile.selected_roles ? (typeof profile.selected_roles === 'string' ? JSON.parse(profile.selected_roles) : profile.selected_roles) : [],
            employmentTypes: profile.employment_types ? (typeof profile.employment_types === 'string' ? JSON.parse(profile.employment_types) : profile.employment_types) : [],
        };
    };

    const userData = getUserData();
    const hasRoleCategories = Array.isArray(userData.rolesOpenTo) && userData.rolesOpenTo.length > 0;
    const hasRoleTitles = Array.isArray(userData.roleTitles) && userData.roleTitles.length > 0;
    const noRolePreferenceData = !hasRoleCategories && !hasRoleTitles;

    const openEdit = (section) => {
        setActiveSection(section);
        // seed form from current data
        if (section === 'profile') {
            setEditForm((p) => ({
                ...p,
                firstName: (profileInfo?.firstname) || '',
                lastName: (profileInfo?.lastname) || '',
                headline: (profileInfo?.headline) || '',
                country: (user?.user_metadata?.country || profileInfo?.country) || '',
                countryCode: (user?.user_metadata?.country_code || profileInfo?.country_code) || '',
                provinceState: (user?.user_metadata?.province_state || profileInfo?.province_state) || '',
                linkedinUrl: (profileInfo?.linkedinurl) || '',
                profileVisibility: (profileInfo?.profile_visibility) || 'public',
            }));
        } else if (section === 'about') {
            setEditForm((p) => ({ ...p, aboutText: userData.aboutText || '' }));
        } else if (section === 'skills') {
            setEditForm((p) => ({ ...p, skills: userData.skills || [], languages: userData.languages || [] }));
        } else if (section === 'aboutSkills') {
            setEditForm((p) => ({
                ...p,
                aboutText: userData.aboutText || '',
                // Normalize skills into objects like onboarding uses
                skills: (userData.skills || []).map((s) => (typeof s === 'string' ? { name: s, level: null } : s)),
                languages: userData.languages || [],
            }));
        } else if (section === 'roles') {
            setEditForm((p) => ({
                ...p,
                rolesOpenTo: userData.rolesOpenTo || [],
                roleTitles: userData.roleTitles || [],
                employmentTypes: userData.employmentTypes || [],
                _roleInput: '',
            }));
        } else if (section === 'experience') {
            setEditForm((p) => ({
                ...p,
                experiences: userData.workExperiences || [],
            }));
        } else if (section === 'education') {
            setEditForm((p) => ({ ...p, educations: userData.educations || [] }));
        } else if (section === 'certifications') {
            setEditForm((p) => ({ ...p, certifications: userData.certifications || [] }));
        } else if (section === 'languages') {
            setEditForm((p) => ({ ...p, languages: userData.languages || [] }));
        }
        setIsEditOpen(true);
    };

    const handleProfileImageClick = () => {
        if (!isEmployerView && fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const updateCollectionItem = (collectionKey, idx, updates, createItem) => {
        setEditForm((prev) => {
            const list = Array.isArray(prev[collectionKey]) ? [...prev[collectionKey]] : [];
            const current = list[idx] ? { ...list[idx] } : createItem();
            list[idx] = { ...current, ...updates };
            return { ...prev, [collectionKey]: list };
        });
    };

    const addCollectionItem = (collectionKey, createItem, scrollCallback) => {
        setEditForm((prev) => {
            const newList = [...(Array.isArray(prev[collectionKey]) ? prev[collectionKey] : []), createItem()];
            // Scroll to the newly added item after state update
            if (scrollCallback) {
                setTimeout(() => {
                    scrollCallback(newList.length - 1);
                }, 100);
            }
            return {
                ...prev,
                [collectionKey]: newList,
            };
        });
    };

    const removeCollectionItem = (collectionKey, idx) => {
        setEditForm((prev) => {
            const list = Array.isArray(prev[collectionKey]) ? [...prev[collectionKey]] : [];
            if (list.length <= 1) {
                // Clean up all refs when clearing the list
                if (collectionKey === 'experiences') {
                    experienceItemRefs.current = {};
                } else if (collectionKey === 'educations') {
                    educationItemRefs.current = {};
                } else if (collectionKey === 'certifications') {
                    certificationItemRefs.current = {};
                }
                return { ...prev, [collectionKey]: [] };
            }
            list.splice(idx, 1);
            // React will automatically reassign refs on re-render, so we don't need to manually shift them
            return { ...prev, [collectionKey]: list };
        });
    };

    const handleExperienceChange = (idx, updates) => updateCollectionItem('experiences', idx, updates, createEmptyExperience);
    const handleEducationChange = (idx, updates) => updateCollectionItem('educations', idx, updates, createEmptyEducation);
    const handleCertificationChange = (idx, updates) => updateCollectionItem('certifications', idx, updates, createEmptyCertification);

    // Refs for scrolling to newly added items
    const experienceItemRefs = useRef({});
    const educationItemRefs = useRef({});
    const certificationItemRefs = useRef({});

    const scrollToExperience = useCallback((idx) => {
        const ref = experienceItemRefs.current[idx];
        if (ref) {
            ref.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
    }, []);

    const scrollToEducation = useCallback((idx) => {
        const ref = educationItemRefs.current[idx];
        if (ref) {
            ref.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
    }, []);

    const scrollToCertification = useCallback((idx) => {
        const ref = certificationItemRefs.current[idx];
        if (ref) {
            ref.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
    }, []);

    const addExperienceRow = () => addCollectionItem('experiences', createEmptyExperience, scrollToExperience);
    const addEducationRow = () => addCollectionItem('educations', createEmptyEducation, scrollToEducation);
    const addCertificationRow = () => addCollectionItem('certifications', createEmptyCertification, scrollToCertification);

    const removeExperienceRow = (idx) => removeCollectionItem('experiences', idx);
    const removeEducationRow = (idx) => removeCollectionItem('educations', idx);
    const removeCertificationRow = (idx) => removeCollectionItem('certifications', idx);

    const experienceList = Array.isArray(editForm.experiences) && editForm.experiences.length > 0 ? editForm.experiences : [createEmptyExperience()];
    const educationList = Array.isArray(editForm.educations) && editForm.educations.length > 0 ? editForm.educations : [createEmptyEducation()];
    const certificationList = Array.isArray(editForm.certifications) && editForm.certifications.length > 0 ? editForm.certifications : [createEmptyCertification()];
    const experienceCount = Array.isArray(editForm.experiences) ? editForm.experiences.length : 0;
    const educationCount = Array.isArray(editForm.educations) ? editForm.educations.length : 0;
    const certificationCount = Array.isArray(editForm.certifications) ? editForm.certifications.length : 0;

    const handleProfileImageUpload = async (event) => {
        const file = event.target.files?.[0];
        if (!file || !user) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            showError('Please select an image file');
            return;
        }

        // Validate file size (e.g., max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            showError('Image size must be less than 5MB');
            return;
        }

        try {
            setProfileImageUploading(true);
            // Store in profile images bucket
            const bucket = process.env.REACT_APP_PROFILE_IMAGES_BUCKET || "profile-images";
            const filePath = `${user.id}/${Date.now()}_${file.name}`;
            const { error: uploadError } = await supabase.storage.from(bucket).upload(filePath, file, {
                upsert: true,
                contentType: file.type || "image/jpeg",
            });
            if (uploadError) throw uploadError;

            // Get public URL
            const { data: publicData } = supabase.storage.from(bucket).getPublicUrl(filePath);
            const publicUrl = publicData?.publicUrl || "";

            // Save reference in user metadata
            const { error: metaError } = await supabase.auth.updateUser({
                data: {
                    profile_image_url: publicUrl,
                    profile_image_path: `${bucket}/${filePath}`,
                    profile_image_filename: file.name,
                },
            });
            if (metaError) throw metaError;

            // Also update users_profiles table
            const { error: profileError } = await supabase
                .from('users_profiles')
                .update({ profile_image_url: publicUrl })
                .eq('profileid', user.id);
            
            if (profileError) {
                console.warn('Failed to update users_profiles table:', profileError);
                // Don't throw here, as the metadata update succeeded
            }

            // Clear profile cache and refresh
            try {
                sessionStorage.removeItem(`profile_${user.id}`);
            } catch (_) {}
            
            await getProfileInfo();
            showSuccess("Profile image updated successfully");
        } catch (e) {
            console.error("Profile image upload error", e);
            showError(e.message || "Failed to upload profile image");
        } finally {
            setProfileImageUploading(false);
            // Reset file input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const saveEdit = async () => {
        try {
            if (activeSection === 'profile') {
                // Build location string from country and province/state for backward compatibility
                const locationString = [editForm.provinceState, editForm.country].filter(Boolean).join(', ') || '';
                const myProfile = {
                    ...(user?.user_metadata?.my_profile || {}),
                    profile_personal_info: {
                        headline: editForm.headline,
                        linkedinUrl: editForm.linkedinUrl,
                        locationCityCountry: locationString,
                    },
                    profile_complete: {
                        ...(user?.user_metadata?.my_profile?.profile_complete || {}),
                        visibility: editForm.profileVisibility,
                    },
                };
                const { error } = await supabase.auth.updateUser({
                    data: {
                        first_name: editForm.firstName,
                        last_name: editForm.lastName,
                        headline: editForm.headline,
                        country: editForm.country,
                        country_code: editForm.countryCode,
                        province_state: editForm.provinceState,
                        location_city_country: locationString,
                        linkedin_url: editForm.linkedinUrl,
                        my_profile: myProfile,
                    },
                });
                if (error) throw error;
                // Also reflect into users_profiles
                await supabase
                    .from('users_profiles')
                    .update({
                        firstname: editForm.firstName,
                        lastname: editForm.lastName,
                        headline: editForm.headline,
                        country: editForm.country,
                        country_code: editForm.countryCode,
                        province_state: editForm.provinceState,
                        location_city_country: locationString,
                        linkedinurl: editForm.linkedinUrl,
                        profile_visibility: editForm.profileVisibility,
                    })
                    .eq('profileid', user.id);
            } else if (activeSection === 'about') {
                const myProfile = {
                    ...(user?.user_metadata?.my_profile || {}),
                    profile_complete: {
                        ...(user?.user_metadata?.my_profile?.profile_complete || {}),
                        about: editForm.aboutText,
                    },
                };
                const { error } = await supabase.auth.updateUser({
                    data: {
                        profile_about: editForm.aboutText,
                        my_profile: myProfile,
                    },
                });
                if (error) throw error;
                await supabase
                    .from('users_profiles')
                    .update({ profile_about: editForm.aboutText })
                    .eq('profileid', user.id);
            } else if (activeSection === 'skills') {
                const myProfile = {
                    ...(user?.user_metadata?.my_profile || {}),
                    profile_skills: {
                        skills: editForm.skills,
                        languages: editForm.languages,
                        carLicense: user?.user_metadata?.my_profile?.profile_skills?.carLicense ?? null,
                    },
                };
                const { error } = await supabase.auth.updateUser({
                    data: { my_profile: myProfile, profile_skills: editForm.skills, languages: editForm.languages },
                });
                if (error) throw error;
                await supabase
                    .from('users_profiles')
                    .update({
                        skills: JSON.stringify(editForm.skills),
                        languages: JSON.stringify(editForm.languages),
                    })
                    .eq('profileid', user.id);
            } else if (activeSection === 'aboutSkills') {
                // Save both About and Skills like onboarding
                const myProfile = {
                    ...(user?.user_metadata?.my_profile || {}),
                    profile_complete: {
                        ...(user?.user_metadata?.my_profile?.profile_complete || {}),
                        about: editForm.aboutText,
                    },
                    profile_skills: {
                        skills: editForm.skills,
                        languages: editForm.languages,
                        carLicense: user?.user_metadata?.my_profile?.profile_skills?.carLicense ?? null,
                    },
                };
                const { error } = await supabase.auth.updateUser({
                    data: { my_profile: myProfile, profile_about: editForm.aboutText, profile_skills: editForm.skills, languages: editForm.languages },
                });
                if (error) throw error;
                await supabase
                    .from('users_profiles')
                    .update({
                        profile_about: editForm.aboutText,
                        skills: JSON.stringify(editForm.skills),
                        languages: JSON.stringify(editForm.languages),
                    })
                    .eq('profileid', user.id);
            } else if (activeSection === 'roles') {
                const categories = Array.isArray(editForm.rolesOpenTo) ? editForm.rolesOpenTo : [];
                const roleTitles = Array.isArray(editForm.roleTitles) ? editForm.roleTitles : [];
                const employmentTypes = Array.isArray(editForm.employmentTypes) ? editForm.employmentTypes : [];
                const myProfile = {
                    ...(user?.user_metadata?.my_profile || {}),
                    profile_role: {
                        categories,
                        roles: roleTitles,
                        employmentTypes,
                    },
                };
                const { error } = await supabase.auth.updateUser({
                    data: { my_profile: myProfile },
                });
                if (error) throw error;
                await supabase
                    .from('users_profiles')
                    .update({
                        selected_categories: JSON.stringify(categories),
                        selected_roles: JSON.stringify(roleTitles),
                        employment_types: JSON.stringify(employmentTypes),
                    })
                    .eq('profileid', user.id);
            } else if (activeSection === 'experience') {
                const myProfile = {
                    ...(user?.user_metadata?.my_profile || {}),
                    profile_experience: {
                        experiences: editForm.experiences,
                        educations: user?.user_metadata?.my_profile?.profile_experience?.educations || [],
                        certifications: user?.user_metadata?.my_profile?.profile_experience?.certifications || [],
                    },
                };
                const { error } = await supabase.auth.updateUser({
                    data: { my_profile: myProfile },
                });
                if (error) throw error;
                await supabase
                    .from('users_profiles')
                    .update({
                        work_experiences: JSON.stringify(editForm.experiences),
                    })
                    .eq('profileid', user.id);
            } else if (activeSection === 'education') {
                const myProfile = {
                    ...(user?.user_metadata?.my_profile || {}),
                    profile_experience: {
                        experiences: user?.user_metadata?.my_profile?.profile_experience?.experiences || [],
                        educations: editForm.educations,
                        certifications: user?.user_metadata?.my_profile?.profile_experience?.certifications || [],
                    },
                };
                const { error } = await supabase.auth.updateUser({
                    data: { my_profile: myProfile },
                });
                if (error) throw error;
                await supabase
                    .from('users_profiles')
                    .update({
                        educations: JSON.stringify(editForm.educations),
                    })
                    .eq('profileid', user.id);
            } else if (activeSection === 'certifications') {
                const myProfile = {
                    ...(user?.user_metadata?.my_profile || {}),
                    profile_experience: {
                        experiences: user?.user_metadata?.my_profile?.profile_experience?.experiences || [],
                        educations: user?.user_metadata?.my_profile?.profile_experience?.educations || [],
                        certifications: editForm.certifications,
                    },
                };
                const { error } = await supabase.auth.updateUser({
                    data: { my_profile: myProfile },
                });
                if (error) throw error;
                await supabase
                    .from('users_profiles')
                    .update({
                        certifications: JSON.stringify(editForm.certifications),
                    })
                    .eq('profileid', user.id);
            } else if (activeSection === 'languages') {
                const myProfile = {
                    ...(user?.user_metadata?.my_profile || {}),
                    profile_skills: {
                        skills: user?.user_metadata?.my_profile?.profile_skills?.skills || [],
                        languages: editForm.languages,
                        carLicense: user?.user_metadata?.my_profile?.profile_skills?.carLicense ?? null,
                    },
                };
                const { error } = await supabase.auth.updateUser({
                    data: { my_profile: myProfile },
                });
                if (error) throw error;
                await supabase
                    .from('users_profiles')
                    .update({
                        languages: JSON.stringify(editForm.languages),
                    })
                    .eq('profileid', user.id);
            }

            // Refresh ProfileInfo context and cached data so UI reflects changes
            try { sessionStorage.removeItem(`profile_${user.id}`); } catch (_) {}
            await getProfileInfo();
            setIsEditOpen(false);
            // Soft refresh: navigate to same route to re-pull ProfileInfo context if needed
            // or you can optimistically update local UI
        } catch (e) {
            console.error('Save failed', e);
            alert(e.message || 'Failed to save');
        }
    };

    const EditIcon = () => (
        <svg className="w-4 h-4 text-gray-500 hover:text-gray-700 cursor-pointer" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
    );

    const LinkedInIcon = () => (
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
        </svg>
    );


    return (
        <>
        <style>{`
            @media (max-width: 1024px) {
                .my-profile-container {
                    padding: 1rem !important;
                    padding-top: 1rem !important;
                    padding-bottom: 1rem !important;
                }
                .my-profile-header {
                    padding: 1rem !important;
                    margin-bottom: 1rem !important;
                }
                .my-profile-header h2 {
                    font-size: 0.875rem !important;
                    margin-bottom: 0.75rem !important;
                }
                .my-profile-header-content {
                    flex-direction: column !important;
                    gap: 1rem !important;
                }
                .my-profile-header-info {
                    width: 100% !important;
                }
                .my-profile-header-info h1 {
                    font-size: 1.25rem !important;
                }
                .my-profile-header-info .text-sm {
                    font-size: 0.75rem !important;
                }
                .my-profile-grid {
                    grid-template-columns: 1fr !important;
                    gap: 1rem !important;
                }
                .my-profile-card {
                    padding: 1rem !important;
                }
                .my-profile-card h2 {
                    font-size: 1rem !important;
                    margin-bottom: 0.75rem !important;
                }
                .my-profile-card h3 {
                    font-size: 0.875rem !important;
                    margin-bottom: 0.5rem !important;
                }
                .my-profile-card p {
                    font-size: 0.875rem !important;
                    line-height: 1.5 !important;
                }
                .my-profile-skills-grid {
                    grid-template-columns: 1fr !important;
                    gap: 0.5rem !important;
                }
                .my-profile-skills-grid ul {
                    width: 100% !important;
                }
                .my-profile-category-pills {
                    gap: 0.5rem !important;
                }
                .my-profile-category-pills span {
                    font-size: 0.75rem !important;
                    padding: 0.375rem 0.75rem !important;
                }
                .my-profile-experience-item {
                    padding-left: 0.75rem !important;
                    border-left-width: 2px !important;
                }
                .my-profile-experience-item h3 {
                    font-size: 0.875rem !important;
                }
                .my-profile-experience-item p {
                    font-size: 0.75rem !important;
                }
            }
            .role-title-pill {
                background-color: #f3f4f6 !important;
                color: #111827 !important;
                border: none !important;
            }
        `}</style>
        <div className="min-h-screen py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 my-profile-container">
                {/* Header for employer view */}
                {isEmployerView && (
                    <div className="mb-8">
                        <button 
                            type="button"
                            onClick={() => navigate(-1)}
                            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
                        >
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                            Back
                        </button>
                        <h1 className="text-3xl font-bold text-gray-900">Candidate Profile</h1>
                    </div>
                )}
                
                {/* Top header block - full-bleed white bar */}
                <div className="-mx-4 sm:-mx-6 lg:-mx-8 bg-white shadow-sm border-b border-gray-200 px-4 sm:px-6 lg:px-8 py-6 mb-8 rounded-none my-profile-header">
                    <div className="flex items-start justify-between mb-4">
                        <h2 className="text-sm font-medium text-gray-700">Profile</h2>
                        {!isEmployerView && (
                            <button
                                type="button"
                                onClick={() => openEdit('profile')}
                                aria-label="Edit profile"
                                className="p-2 rounded hover:bg-gray-50 text-gray-600"
                            >
                                <EditIcon />
                            </button>
                        )}
                    </div>
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between my-profile-header-content">
                        <div className="flex items-start space-x-5 my-profile-header-info">
                            <div className="relative">
                                <div 
                                    className={`w-14 h-14 md:w-16 md:h-16 bg-gray-200 rounded-full flex items-center justify-center overflow-hidden ${
                                        !isEmployerView ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''
                                    } ${profileImageUploading ? 'opacity-50' : ''}`}
                                    onClick={handleProfileImageClick}
                                    title={!isEmployerView ? "Click to update profile image" : ""}
                                >
                                    {userData.profileImageUrl ? (
                                        <img src={userData.profileImageUrl} alt="Profile" className="w-full h-full object-cover" />
                                    ) : (
                                        <span className="text-lg md:text-xl font-medium text-gray-500">
                                            {userData.fullName ? userData.fullName.split(' ').map(n => n[0]).join('').toUpperCase() : 'U'}
                                        </span>
                                    )}
                                    {profileImageUploading && (
                                        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 rounded-full">
                                            <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                        </div>
                                    )}
                                    {!isEmployerView && !profileImageUploading && (
                                        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 hover:bg-opacity-30 transition-all rounded-full">
                                            <svg className="w-6 h-6 text-white opacity-0 hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                                            </svg>
                                        </div>
                                    )}
                                </div>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    onChange={handleProfileImageUpload}
                                    className="hidden"
                                />
                            </div>
                            <div>
                                <div className="flex items-center gap-2">
                                    <h1 className="text-xl md:text-2xl font-semibold text-gray-900">{userData.fullName}</h1>
                                    {userData.hpcsaRegNo && typeof userData.hpcsaRegNo === 'string' && userData.hpcsaRegNo.trim().length > 0 && (
                                        <img
                                            src="/SealCheck.svg"
                                            alt={userData.hpcsaVerified ? "HPCSA Verified" : "HPCSA Pending Verification"}
                                            className={`w-6 h-6 transition-all ${
                                                userData.hpcsaVerified 
                                                    ? 'opacity-100' 
                                                    : 'opacity-30 grayscale'
                                            }`}
                                            title={userData.hpcsaVerified ? "HPCSA Verified" : "HPCSA Verification Pending"}
                                        />
                                    )}
                                </div>
                                {userData.headline && userData.headline.trim() && (
                                    <p className="mt-1 text-sm text-gray-600 font-medium">{userData.headline}</p>
                                )}
                                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-gray-600">
                                    {(userData.email && (userData.location || userData.linkedinUrl)) && <span className="text-gray-300">|</span>}
                                    {userData.location && (
                                        <span className="text-sm inline-flex items-center gap-1">
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 22s8-4.5 8-12A8 8 0 104 10c0 7.5 8 12 8 12z"/></svg>
                                            {userData.location}
                                        </span>
                                    )}
                                    {(userData.linkedinUrl && (userData.email || userData.location)) && <span className="text-gray-300">|</span>}
                                    {userData.linkedinUrl && (
                                        <a href={userData.linkedinUrl} target="_blank" rel="noopener noreferrer" className="text-sm inline-flex items-center gap-2 hover:underline">
                                            <LinkedInIcon />
                                            <span>LinkedIn</span>
                                        </a>
                                    )}
                                </div>
                            </div>
                        </div>
                        {/* Spacer/right actions kept minimal per mock */}
                    </div>
                </div>
                
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 my-profile-grid">
                    {/* Left Column - Main Content */}
                    <div className="lg:col-span-2 space-y-8">
                        {/* About Section */}

                        {/* About + Skills Section (combined) */}
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 my-profile-card">
                            <div className="flex justify-between items-start mb-4">
                                <h2 className="text-xl font-semibold text-gray-900">About</h2>
                                {!isEmployerView && (
                                    <button type="button" onClick={() => openEdit('aboutSkills')} className="p-1 text-gray-600 hover:text-gray-800">
                                        <EditIcon />
                                    </button>
                                )}
                            </div>
                            <div className="text-gray-700 leading-relaxed">
                                {userData.aboutText ? (
                                    userData.aboutText.split('\n').map((line, index, array) => (
                                        <span key={index}>
                                            {line}
                                            {index < array.length - 1 && <br />}
                                        </span>
                                    ))
                                ) : (
                                    'No about information available. Click edit to add your professional summary.'
                                )}
                            </div>

                            {/* Divider */}
                            <div className="border-t border-gray-200 my-6"></div>

                            <h3 className="text-xl font-semibold text-gray-900 mb-4">Skills</h3>
                            {userData.skills && userData.skills.length > 0 ? (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-profile-skills-grid">
                                    <ul className="space-y-2">
                                        {userData.skills.slice(0, Math.ceil(userData.skills.length / 2)).map((skill, index) => (
                                            <li key={index} className="flex items-center">
                                                <span className="w-2 h-2 bg-gray-400 rounded-full mr-3"></span>
                                                <span className="text-gray-700">
                                                    {typeof skill === 'string' ? skill : skill.name}
                                                    {typeof skill === 'object' && skill.level && (
                                                        <span className="text-sm text-gray-500 ml-2">({skill.level})</span>
                                                    )}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                    <ul className="space-y-2">
                                        {userData.skills.slice(Math.ceil(userData.skills.length / 2)).map((skill, index) => (
                                            <li key={index} className="flex items-center">
                                                <span className="w-2 h-2 bg-gray-400 rounded-full mr-3"></span>
                                                <span className="text-gray-700">
                                                    {typeof skill === 'string' ? skill : skill.name}
                                                    {typeof skill === 'object' && skill.level && (
                                                        <span className="text-sm text-gray-500 ml-2">({skill.level})</span>
                                                    )}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ) : (
                                <p className="text-gray-500 italic">No skills added yet. Click edit to add your skills.</p>
                            )}
                        </div>

                        {/* Experience Section */}
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 my-profile-card">
                            <div className="flex justify-between items-start mb-4">
                                <h2 className="text-xl font-semibold text-gray-900">Experience</h2>
                                {!isEmployerView && (
                                    <button type="button" onClick={() => openEdit('experience')} className="p-1 text-gray-600 hover:text-gray-800">
                                        <EditIcon />
                                    </button>
                                )}
                            </div>
                            {userData.workExperiences && userData.workExperiences.length > 0 ? (
                                <div className="space-y-6">
                                    {userData.workExperiences.map((experience, index) => {
                                        const startDateFormatted = formatDateMonthYear(experience.startDate);
                                        const endDateFormatted = experience.isCurrent ? 'Present' : formatDateMonthYear(experience.endDate);
                                        const duration = calculateDuration(experience.startDate, experience.endDate, experience.isCurrent);
                                        const dateRange = startDateFormatted && endDateFormatted 
                                            ? `${startDateFormatted} to ${endDateFormatted}${duration ? ` · ${duration}` : ''}`
                                            : '';
                                        const employmentTypeDisplay = formatEmploymentType(experience.employmentType);
                                        const locationParts = [];
                                        if (experience.provinceState) locationParts.push(experience.provinceState);
                                        if (experience.country) locationParts.push(experience.country);
                                        const locationDisplay = locationParts.join(', ');
                                        
                                        return (
                                            <div key={index} className="border-l-4 border-gray-400 pl-4 my-profile-experience-item">
                                                <div className="flex justify-between items-start mb-2">
                                                    <h3 className="font-semibold text-gray-900 underline">{experience.title || '[Role]'}</h3>
                                                    {dateRange && (
                                                        <span className="text-gray-500 text-sm whitespace-nowrap ml-4">{dateRange}</span>
                                                    )}
                                                </div>
                                                <p className="text-gray-600 text-sm mb-1">
                                                    {experience.company || 'Company Name'}
                                                    {employmentTypeDisplay && ` · ${employmentTypeDisplay}`}
                                                </p>
                                                {locationDisplay && (
                                                    <p className="text-gray-500 text-sm mb-2">{locationDisplay}</p>
                                                )}
                                                {experience.description && (
                                                    <p className="text-gray-700 mt-2 text-sm">
                                                        {experience.description}
                                                        <span className="text-blue-600 cursor-pointer ml-1 underline">See More..</span>
                                                    </p>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <p className="text-gray-500 italic">No work experience added yet. Click edit to add your work experience.</p>
                            )}
                        </div>

                        {/* Education Section */}
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 my-profile-card">
                            <div className="flex justify-between items-start mb-4">
                                <h2 className="text-xl font-semibold text-gray-900">Education</h2>
                                {!isEmployerView && (
                                    <button type="button" onClick={() => openEdit('education')} className="p-1 text-gray-600 hover:text-gray-800">
                                        <EditIcon />
                                    </button>
                                )}
                            </div>
                            {userData.educations && userData.educations.length > 0 ? (
                                <div className="space-y-6">
                                    {userData.educations.map((education, index) => {
                                        const startDateFormatted = formatDateMonthYear(education.startDate);
                                        const endDateFormatted = education.isCurrent ? 'Present' : formatDateMonthYear(education.endDate);
                                        const duration = calculateDuration(education.startDate, education.endDate, education.isCurrent);
                                        const dateRange = startDateFormatted && endDateFormatted 
                                            ? `${startDateFormatted} to ${endDateFormatted}${duration ? ` · ${duration}` : ''}`
                                            : '';
                                        const locationParts = [];
                                        if (education.provinceState) locationParts.push(education.provinceState);
                                        if (education.country) locationParts.push(education.country);
                                        const locationDisplay = locationParts.join(', ');
                                        
                                        return (
                                            <div key={index} className="border-l-4 border-gray-400 pl-4 my-profile-experience-item">
                                                <div className="flex justify-between items-start mb-2">
                                                    <h3 className="font-semibold text-gray-900 underline">
                                                        {education.fieldOfStudy || education.degree || '[Course name]'}
                                                    </h3>
                                                    {dateRange && (
                                                        <span className="text-gray-500 text-sm whitespace-nowrap ml-4">{dateRange}</span>
                                                    )}
                                                </div>
                                                <p className="text-gray-600 text-sm mb-1">{education.school || 'Institution Name'}</p>
                                                {locationDisplay && (
                                                    <p className="text-gray-500 text-sm mb-2">{locationDisplay}</p>
                                                )}
                                                {education.description && (
                                                    <p className="text-gray-700 mt-2 text-sm">
                                                        {education.description}
                                                        <span className="text-blue-600 cursor-pointer ml-1 underline">See More..</span>
                                                    </p>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <p className="text-gray-500 italic">No education information added yet. Click edit to add your education.</p>
                            )}
                        </div>

                        {/* Certifications Section */}
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 my-profile-card">
                            <div className="flex justify-between items-start mb-4">
                                <h2 className="text-xl font-semibold text-gray-900">Certifications</h2>
                                {!isEmployerView && (
                                    <button type="button" onClick={() => openEdit('certifications')} className="p-1 text-gray-600 hover:text-gray-800">
                                        <EditIcon />
                                    </button>
                                )}
                            </div>
                            {userData.certifications && userData.certifications.length > 0 ? (
                                <div className="space-y-4">
                                    {userData.certifications.map((certification, index) => (
                                        <div key={index} className="border-l-4 border-gray-400 pl-4">
                                            <h3 className="font-semibold text-gray-900">
                                                {certification.courseName || '[Course name]'}
                                            </h3>
                                            <p className="text-gray-600 font-medium">
                                                {certification.issuingOrg || 'Institution Name'}
                                            </p>
                                            <p className="text-gray-500 text-sm">
                                                {certification.issueDate ? `Issued: ${certification.issueDate}` : 'Issued: date'}
                                            </p>
                                            {certification.credentialId && (
                                                <p className="text-gray-500 text-sm">
                                                    Credential ID: {certification.credentialId}
                                                </p>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-gray-500 italic">No certifications added yet. Click edit to add your certifications.</p>
                            )}
                        </div>
                    </div>

                    {/* Right Column - Supplementary Information */}
                    <div className="space-y-8">
                        {/* Roles Open To Section */}
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 my-profile-card">
                            <div className="flex justify-between items-start mb-4">
                                <h2 className="text-xl font-semibold text-gray-900">Roles open to:</h2>
                                {!isEmployerView && <span onClick={() => openEdit('roles')}><EditIcon /></span>}
                            </div>
                            {noRolePreferenceData ? (
                                <p className="text-gray-500 italic">No role preferences captured yet. Click edit to add your role interests.</p>
                            ) : (
                                <div className="space-y-4">
                                    {hasRoleCategories && (
                                        <div>
                                            <p className="text-sm text-gray-500 mb-2">Role categories</p>
                                            <div className="flex flex-wrap gap-2 my-profile-category-pills">
                                                {userData.rolesOpenTo.map((category, index) => (
                                                    <span
                                                        key={`${category}-${index}`}
                                                        className={`px-3 py-1 rounded-full text-sm font-medium category-pill ${getCategoryClass(category)}`}
                                                    >
                                                        {category}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {hasRoleTitles && (
                                        <div>
                                            <div className="flex flex-wrap gap-2 my-profile-category-pills">
                                                {userData.roleTitles.map((role) => (
                                                    <span key={role} className="category-pill role-title-pill">
                                                        {role}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Languages Spoken Section */}
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 my-profile-card">
                            <div className="flex justify-between items-start mb-4">
                                <h2 className="text-xl font-semibold text-gray-900">Languages spoken</h2>
                                {!isEmployerView && <span onClick={() => openEdit('languages')}><EditIcon /></span>}
                            </div>
                            {userData.languages && userData.languages.length > 0 ? (
                                <ul className="space-y-2">
                                    {userData.languages.map((language, index) => (
                                        <li key={index} className="flex items-center">
                                            <span className="w-2 h-2 bg-gray-400 rounded-full mr-3"></span>
                                            <span className="text-gray-700">{language}</span>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <p className="text-gray-500 italic">No languages added yet. Click edit to add languages you speak.</p>
                            )}
                        </div>
                    </div>

                </div>
            </div>
        </div>
        <MyProfile.ModalPortal
            isOpen={isEditOpen}
            section={activeSection}
            editForm={editForm}
            setEditForm={setEditForm}
            onClose={() => setIsEditOpen(false)}
            onSave={saveEdit}
            experienceControls={{
                list: experienceList,
                count: experienceCount,
                onAdd: addExperienceRow,
                onChange: handleExperienceChange,
                onRemove: removeExperienceRow,
                itemRefs: experienceItemRefs,
            }}
            educationControls={{
                list: educationList,
                count: educationCount,
                onAdd: addEducationRow,
                onChange: handleEducationChange,
                onRemove: removeEducationRow,
                itemRefs: educationItemRefs,
            }}
            certificationControls={{
                list: certificationList,
                count: certificationCount,
                onAdd: addCertificationRow,
                onChange: handleCertificationChange,
                onRemove: removeCertificationRow,
                itemRefs: certificationItemRefs,
            }}
        />
        </>
    );
};

// Edit Modal
// Lightweight modal for editing different sections
// Reuses rounded-full inputs to match existing style
const EditModal = ({ open, title, children, onClose, onSave }) => {
    if (!open) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 overflow-y-auto">
            <div className="bg-white w-full max-w-2xl rounded-2xl border border-gray-200 my-auto">
                <div className="sticky top-0 bg-white rounded-t-2xl px-6 pt-6 pb-4 border-b border-gray-200 z-10">
                    <div className="flex items-center justify-between">
                        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
                        <button onClick={onClose} className="text-gray-500 hover:text-gray-800">✕</button>
                    </div>
                </div>
                <div className="px-6 py-4 max-h-[calc(100vh-200px)] overflow-y-auto">
                    {children}
                </div>
                <div className="sticky bottom-0 bg-white rounded-b-2xl px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
                    <button onClick={onClose} className="px-4 py-2 rounded-full border-2 border-gray-300">Cancel</button>
                    <button onClick={onSave} className="px-4 py-2 rounded-full bg-black text-white">Save</button>
                </div>
            </div>
        </div>
    );
};

// Inject modal renderer at bottom of component tree
MyProfile.ModalPortal = function ModalPortal({
    isOpen,
    section,
    editForm,
    setEditForm,
    onClose,
    onSave,
    experienceControls = {},
    educationControls = {},
    certificationControls = {},
}) {
    if (!isOpen) return null;
    const {
        list: experienceList = [],
        count: experienceCount = 0,
        onAdd: addExperienceRow = () => {},
        onChange: handleExperienceChange = () => {},
        onRemove: removeExperienceRow = () => {},
        itemRefs: experienceItemRefs = { current: {} },
    } = experienceControls;
    const {
        list: educationList = [],
        count: educationCount = 0,
        onAdd: addEducationRow = () => {},
        onChange: handleEducationChange = () => {},
        onRemove: removeEducationRow = () => {},
        itemRefs: educationItemRefs = { current: {} },
    } = educationControls;
    const {
        list: certificationList = [],
        count: certificationCount = 0,
        onAdd: addCertificationRow = () => {},
        onChange: handleCertificationChange = () => {},
        onRemove: removeCertificationRow = () => {},
        itemRefs: certificationItemRefs = { current: {} },
    } = certificationControls;
    const titleMap = {
        profile: 'Edit Profile',
        about: 'Edit About',
        skills: 'Edit Skills & Languages',
        roles: 'Edit Roles Open To',
        experience: 'Edit Experience',
        education: 'Edit Education',
        certifications: 'Edit Certifications',
        languages: 'Edit Languages',
    };

    return (
        <EditModal open={isOpen} title={titleMap[section]} onClose={onClose} onSave={onSave}>
            {section === 'profile' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm text-gray-700 mb-1">First name</label>
                        <input className="w-full rounded-full border border-gray-300 px-4 py-2" value={editForm.firstName} onChange={(e) => setEditForm((p) => ({ ...p, firstName: e.target.value }))} />
                    </div>
                    <div>
                        <label className="block text-sm text-gray-700 mb-1">Last name</label>
                        <input className="w-full rounded-full border border-gray-300 px-4 py-2" value={editForm.lastName} onChange={(e) => setEditForm((p) => ({ ...p, lastName: e.target.value }))} />
                    </div>
                    <div className="md:col-span-2">
                        <div className="flex items-center justify-between mb-1">
                            <label className="block text-sm text-gray-700">
                                Headline
                                <span className="relative group inline-block ml-1">
                                    <svg className="w-4 h-4 text-gray-500 cursor-help inline" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                                    </svg>
                                    {/* Tooltip */}
                                    <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block w-max max-w-xs px-3 py-2 text-sm text-white bg-[#1E1E1E]/80 rounded-lg shadow-lg z-10 whitespace-normal">
                                        A short, clear summary of your role or career focus. Example: 'Medical Registrar''
                                        <div className="absolute top-full left-4 -mt-1 w-2 h-2 bg-[#1E1E1E]/80 rotate-45"></div>
                                    </div>
                                </span>
                            </label>
                            <span className="text-xs text-gray-500">{editForm.headline?.length || 0}/100</span>
                        </div>
                        <input 
                            className="w-full rounded-full border border-gray-300 px-4 py-2" 
                            placeholder="e.g. Medical Registrar"
                            value={editForm.headline} 
                            maxLength={100}
                            onChange={(e) => setEditForm((p) => ({ ...p, headline: e.target.value }))} 
                        />
                    </div>
                    <div>
                        <label className="block text-sm text-gray-700 mb-1">Country</label>
                        <Select
                            options={[...countryList().getData()].sort((a, b) => (a.label === 'South Africa' ? -1 : b.label === 'South Africa' ? 1 : a.label.localeCompare(b.label)))}
                            value={countryList().getData().find(c => c.value === editForm.countryCode || c.label === editForm.country) || null}
                            onChange={(selectedOption) => {
                                const code = selectedOption?.value || "";
                                setEditForm((p) => ({
                                    ...p,
                                    country: selectedOption?.label || "",
                                    countryCode: code,
                                    provinceState: ""
                                }));
                            }}
                            placeholder="Select country..."
                            isSearchable
                            classNamePrefix="react-select"
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={{
                                control: (provided, state) => ({
                                    ...provided,
                                    borderRadius: '9999px',
                                    borderColor: state.isFocused ? '#9ca3af' : '#d1d5db',
                                    boxShadow: state.isFocused ? '0 0 0 1px #9ca3af' : 'none',
                                    '&:hover': { borderColor: '#9ca3af' },
                                }),
                                menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
                            }}
                        />
                    </div>
                    <div>
                        <label className="block text-sm text-gray-700 mb-1">Province/State</label>
                        {editForm.countryCode ? (
                            <Select
                                options={State.getStatesOfCountry(editForm.countryCode).map((s) => ({ value: s.isoCode, label: s.name }))}
                                value={editForm.provinceState ? { value: editForm.provinceState, label: editForm.provinceState } : null}
                                onChange={(opt) => setEditForm((prev) => ({ ...prev, provinceState: opt?.label || '' }))}
                                placeholder="Select province/state..."
                                isSearchable
                                classNamePrefix="react-select"
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                styles={{
                                    control: (provided, state) => ({
                                        ...provided,
                                        borderRadius: '9999px',
                                        borderColor: state.isFocused ? '#9ca3af' : '#d1d5db',
                                        boxShadow: state.isFocused ? '0 0 0 1px #9ca3af' : 'none',
                                        '&:hover': { borderColor: '#9ca3af' },
                                    }),
                                    menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
                                }}
                            />
                        ) : (
                            <input
                                className="w-full rounded-full border px-4 py-2 text-sm bg-gray-50 border-gray-300"
                                placeholder="Select country first"
                                value={editForm.provinceState}
                                onChange={(e) => setEditForm((p) => ({ ...p, provinceState: e.target.value }))}
                                disabled
                            />
                        )}
                    </div>
                    <div className="md:col-span-2">
                        <label className="block text-sm text-gray-700 mb-1">LinkedIn URL</label>
                        <input className="w-full rounded-full border border-gray-300 px-4 py-2" value={editForm.linkedinUrl} onChange={(e) => setEditForm((p) => ({ ...p, linkedinUrl: e.target.value }))} />
                    </div>
                    <div className="md:col-span-2">
                        <label className="block text-sm text-gray-700 mb-3">Choose if employers can view your profile*</label>
                        <div className="flex flex-col gap-3">
                            <label className="inline-flex items-center gap-3 text-sm text-gray-800">
                                <input type="radio" name="profile-visibility" className="accent-black" checked={editForm.profileVisibility === 'public'} onChange={() => setEditForm((p) => ({ ...p, profileVisibility: 'public' }))} />
                                Anyone can see it
                            </label>
                            <label className="inline-flex items-center gap-3 text-sm text-gray-800">
                                <input type="radio" name="profile-visibility" className="accent-black" checked={editForm.profileVisibility === 'invisible-current'} onChange={() => setEditForm((p) => ({ ...p, profileVisibility: 'invisible-current' }))} />
                                Invisible to current employers
                            </label>
                            <label className="inline-flex items-center gap-3 text-sm text-gray-800">
                                <input type="radio" name="profile-visibility" className="accent-black" checked={editForm.profileVisibility === 'hidden'} onChange={() => setEditForm((p) => ({ ...p, profileVisibility: 'hidden' }))} />
                                Keep it hidden for now
                            </label>
                        </div>
                    </div>
                </div>
            )}
            {section === 'about' && (
                <div>
                    <label className="block text-sm text-gray-700 mb-1">About</label>
                    <textarea 
                        rows={6} 
                        className="w-full rounded-xl border border-gray-300 px-4 py-2" 
                        value={editForm.aboutText} 
                        maxLength={2000}
                        onChange={(e) => setEditForm((p) => ({ ...p, aboutText: e.target.value }))} 
                    />
                    <div className="text-xs text-gray-500 mt-1 text-right">
                        {(editForm.aboutText || '').length} / 2000
                    </div>
                </div>
            )}
            {section === 'skills' && (
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm text-gray-700 mb-1">Skills (comma-separated)</label>
                        <input className="w-full rounded-full border border-gray-300 px-4 py-2" value={(editForm.skills || []).map((s) => (typeof s === 'string' ? s : s.name)).join(', ')} onChange={(e) => {
                            const arr = e.target.value.split(',').map((s) => s.trim()).filter(Boolean);
                            setEditForm((p) => ({ ...p, skills: arr }));
                        }} />
                    </div>
                    <div>
                        <label className="block text-sm text-gray-700 mb-1">Languages (comma-separated)</label>
                        <input className="w-full rounded-full border border-gray-300 px-4 py-2" value={(editForm.languages || []).join(', ')} onChange={(e) => {
                            const arr = e.target.value.split(',').map((s) => s.trim()).filter(Boolean);
                            setEditForm((p) => ({ ...p, languages: arr }));
                        }} />
                    </div>
                </div>
            )}
            {section === 'aboutSkills' && (
                <div className="space-y-8">
                    {/* About */}
                    <div>
                        <label className="block text-sm text-gray-700 mb-1">About</label>
                        <textarea 
                            rows={6} 
                            className="w-full rounded-xl border border-gray-300 px-4 py-2" 
                            value={editForm.aboutText} 
                            maxLength={2000}
                            onChange={(e) => setEditForm((p) => ({ ...p, aboutText: e.target.value }))} 
                        />
                        <div className="text-xs text-gray-500 mt-1 text-right">
                            {(editForm.aboutText || '').length} / 2000
                        </div>
                    </div>

                    {/* Skills - onboarding style */}
                    <div>
                        <div className="flex items-center justify-between">
                            <label className="block text-sm text-gray-700 mb-2">Which Skills are you most experienced and interested in working with?</label>
                            <span className="text-xs text-gray-500">'Enter' to add Skills</span>
                        </div>
                        <div className="relative">
                            <input
                                className="w-full rounded-full border border-gray-300 px-4 py-2 pr-10"
                                placeholder=""
                                value={editForm._skillInput || ''}
                                onChange={(e) => setEditForm((p) => ({ ...p, _skillInput: e.target.value }))}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        const val = (editForm._skillInput || '').trim();
                                        if (!val) return;
                                        const next = (editForm.skills || []);
                                        if (!next.some((s) => (s.name || s) .toString().toLowerCase() === val.toLowerCase())) {
                                            setEditForm((p) => ({ ...p, skills: [...(p.skills || []), { name: val, level: null }], _skillInput: '' }));
                                        } else {
                                            setEditForm((p) => ({ ...p, _skillInput: '' }));
                                        }
                                    }
                                }}
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">🔍</span>
                        </div>

                        {editForm.skills && editForm.skills.length > 0 && (
                            <div className="mt-4 space-y-4">
                                <div className="grid grid-cols-4 gap-4 px-1 text-sm text-gray-600">
                                    <div></div>
                                    <div className="text-center">Foundational</div>
                                    <div className="text-center">Intermediate</div>
                                    <div className="text-center">Advanced</div>
                                </div>
                                {editForm.skills.map((s, idx) => (
                                    <div key={(s.name || s)} className="grid grid-cols-4 gap-4 items-center py-2 border-b border-gray-100">
                                        <div className="flex items-center gap-3">
                                            <button type="button" className="text-red-700 text-sm" onClick={() => setEditForm((p) => ({ ...p, skills: (p.skills || []).filter((_, i) => i !== idx) }))}>Remove</button>
                                            <span className="text-sm">{typeof s === 'string' ? s : s.name}</span>
                                        </div>
                                        {['foundational','intermediate','advanced'].map((lvl) => (
                                            <div key={lvl} className="flex justify-center">
                                                <input
                                                    type="radio"
                                                    name={`skill-${idx}`}
                                                    checked={(typeof s === 'object' ? s.level : null) === lvl}
                                                    onChange={() => setEditForm((p) => {
                                                        const next = [...(p.skills || [])];
                                                        const current = typeof next[idx] === 'string' ? { name: next[idx], level: null } : next[idx];
                                                        next[idx] = { ...current, level: lvl };
                                                        return { ...p, skills: next };
                                                    })}
                                                    className="h-4 w-4 accent-black"
                                                />
                                            </div>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Languages */}
                    <div>
                        <div className="flex items-center justify-between">
                            <label className="block text-sm text-gray-700 mb-2">What Languages do you speak?</label>
                            <span className="text-xs text-gray-500">'Enter' to add</span>
                        </div>
                        <input
                            className="w-full rounded-full border border-gray-300 px-4 py-2"
                            value={editForm._languageInput || ''}
                            onChange={(e) => setEditForm((p) => ({ ...p, _languageInput: e.target.value }))}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    const val = (editForm._languageInput || '').trim();
                                    if (!val) return;
                                    if (!(editForm.languages || []).includes(val)) {
                                        setEditForm((p) => ({ ...p, languages: [ ...(p.languages || []), val ], _languageInput: '' }));
                                    } else {
                                        setEditForm((p) => ({ ...p, _languageInput: '' }));
                                    }
                                }
                            }}
                        />
                        {editForm.languages && editForm.languages.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-3">
                                {editForm.languages.map((lang, i) => (
                                    <span key={`${lang}-${i}`} className="px-3 py-1 rounded-full bg-gray-100 text-sm flex items-center gap-2">
                                        {lang}
                                        <button className="text-gray-500" onClick={() => setEditForm((p) => ({ ...p, languages: (p.languages || []).filter((_, idx) => idx !== i) }))}>×</button>
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
            {section === 'experience' && (
                <div>
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-2xl font-semibold">Work Experience</h2>
                        <button type="button" className="px-4 py-2 rounded-full border-2 text-sm" onClick={addExperienceRow}>
                            + Add Experience
                        </button>
                    </div>
                    {experienceList.map((exp, idx) => (
                        <div 
                            key={`experience-${idx}`} 
                            ref={(el) => { if (el) experienceItemRefs.current[idx] = el; }}
                            className="space-y-4 mb-8"
                        >
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                                <div>
                                    <label className="block text-sm text-gray-700 mb-1">Company</label>
                                    <input
                                        className="w-full rounded-full border border-gray-300 px-4 py-2"
                                        value={exp.company}
                                        onChange={(e) => handleExperienceChange(idx, { company: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-700 mb-1">Title</label>
                                    <input
                                        className="w-full rounded-full border border-gray-300 px-4 py-2"
                                        value={exp.title}
                                        onChange={(e) => handleExperienceChange(idx, { title: e.target.value })}
                                    />
                                </div>
                            </div>

                            <label className="inline-flex items-center gap-2 text-sm text-gray-800">
                                <input
                                    type="checkbox"
                                    checked={exp.isCurrent}
                                    onChange={(e) => {
                                        handleExperienceChange(idx, { 
                                            isCurrent: e.target.checked,
                                            endDate: e.target.checked ? "" : exp.endDate
                                        });
                                    }}
                                />
                                I currently work here
                            </label>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                                <div>
                                    <label className="block text-sm text-gray-700 mb-1">Start Date</label>
                                    <input
                                        type="date"
                                        className="w-full rounded-full border border-gray-300 px-4 py-2"
                                        value={exp.startDate}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            // Ensure year is only 4 digits
                                            if (value) {
                                                const date = new Date(value);
                                                if (!isNaN(date.getTime())) {
                                                    const year = date.getFullYear();
                                                    // If year has more than 4 digits, restrict it
                                                    if (year > 9999 || year < 1000) {
                                                        // Extract only first 4 digits of year
                                                        const yearStr = year.toString().slice(0, 4);
                                                        const month = String(date.getMonth() + 1).padStart(2, '0');
                                                        const day = String(date.getDate()).padStart(2, '0');
                                                        const formattedDate = `${yearStr}-${month}-${day}`;
                                                        handleExperienceChange(idx, { startDate: formattedDate });
                                                        return;
                                                    }
                                                }
                                            }
                                            handleExperienceChange(idx, { startDate: value });
                                        }}
                                        max="9999-12-31"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-700 mb-1">End Date</label>
                                    <input
                                        type="date"
                                        disabled={exp.isCurrent}
                                        className="w-full rounded-full border border-gray-300 px-4 py-2 disabled:opacity-60"
                                        value={exp.endDate}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            // Ensure year is only 4 digits
                                            if (value) {
                                                const date = new Date(value);
                                                if (!isNaN(date.getTime())) {
                                                    const year = date.getFullYear();
                                                    // If year has more than 4 digits, restrict it
                                                    if (year > 9999 || year < 1000) {
                                                        // Extract only first 4 digits of year
                                                        const yearStr = year.toString().slice(0, 4);
                                                        const month = String(date.getMonth() + 1).padStart(2, '0');
                                                        const day = String(date.getDate()).padStart(2, '0');
                                                        const formattedDate = `${yearStr}-${month}-${day}`;
                                                        handleExperienceChange(idx, { endDate: formattedDate });
                                                        return;
                                                    }
                                                }
                                            }
                                            handleExperienceChange(idx, { endDate: value });
                                        }}
                                        max="9999-12-31"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 mt-4">
                                <div>
                                    <label className="block text-sm text-gray-700 mb-1">Country</label>
                                    <Select
                                        options={[...countryList().getData()].sort((a, b) => (a.label === 'South Africa' ? -1 : b.label === 'South Africa' ? 1 : a.label.localeCompare(b.label)))}
                                        value={countryList().getData().find(c => c.value === exp.countryCode || c.label === exp.country) || null}
                                        onChange={(selectedOption) => {
                                            const code = selectedOption?.value || "";
                                            handleExperienceChange(idx, {
                                                country: selectedOption?.label || "",
                                                countryCode: code,
                                                provinceState: ""
                                            });
                                        }}
                                        placeholder="Select Country"
                                        isSearchable
                                        classNamePrefix="react-select"
                                        styles={{
                                            control: (provided, state) => ({
                                                ...provided,
                                                borderRadius: '9999px',
                                                borderColor: state.isFocused ? '#9ca3af' : '#d1d5db',
                                                boxShadow: state.isFocused ? '0 0 0 1px #9ca3af' : 'none',
                                                '&:hover': { borderColor: '#9ca3af' },
                                            }),
                                        }}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-700 mb-1">Province/State</label>
                                    {exp.countryCode ? (
                                        <Select
                                            options={State.getStatesOfCountry(exp.countryCode).map((s) => ({ value: s.isoCode, label: s.name }))}
                                            value={exp.provinceState ? { value: exp.provinceState, label: exp.provinceState } : null}
                                            onChange={(opt) => handleExperienceChange(idx, { provinceState: opt?.label || '' })}
                                            placeholder="Select Province/State"
                                            isSearchable
                                            classNamePrefix="react-select"
                                            styles={{
                                                control: (provided, state) => ({
                                                    ...provided,
                                                    borderRadius: '9999px',
                                                    borderColor: state.isFocused ? '#9ca3af' : '#d1d5db',
                                                    boxShadow: state.isFocused ? '0 0 0 1px #9ca3af' : 'none',
                                                    '&:hover': { borderColor: '#9ca3af' },
                                                }),
                                            }}
                                        />
                                    ) : (
                                        <input
                                            className="rounded-full border px-4 py-2 w-full text-sm bg-gray-50 border-gray-300"
                                            placeholder="Select country first"
                                            value={exp.provinceState}
                                            onChange={(e) => handleExperienceChange(idx, { provinceState: e.target.value })}
                                            disabled
                                        />
                                    )}
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 mt-4">
                                <div>
                                    <label className="block text-sm text-gray-700 mb-1">Employment Type*</label>
                                    <Select
                                        options={employmentTypeOptions}
                                        value={exp.employmentType ? employmentTypeOptions.find(opt => opt.value === exp.employmentType) || null : null}
                                        onChange={(selectedOption) => {
                                            handleExperienceChange(idx, { employmentType: selectedOption?.value || "" });
                                        }}
                                        placeholder="Select way of working"
                                        isSearchable
                                        classNamePrefix="react-select"
                                        styles={{
                                            control: (provided, state) => ({
                                                ...provided,
                                                borderRadius: '9999px',
                                                borderColor: state.isFocused ? '#9ca3af' : '#d1d5db',
                                                boxShadow: state.isFocused ? '0 0 0 1px #9ca3af' : 'none',
                                                '&:hover': { borderColor: '#9ca3af' },
                                            }),
                                        }}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-700 mb-1">Location Type*</label>
                                    <Select
                                        options={locationTypeOptions}
                                        value={exp.locationType ? locationTypeOptions.find(opt => opt.value === exp.locationType) || null : null}
                                        onChange={(selectedOption) => {
                                            handleExperienceChange(idx, { locationType: selectedOption?.value || "" });
                                        }}
                                        placeholder="Select Location Type"
                                        isSearchable
                                        classNamePrefix="react-select"
                                        styles={{
                                            control: (provided, state) => ({
                                                ...provided,
                                                borderRadius: '9999px',
                                                borderColor: state.isFocused ? '#9ca3af' : '#d1d5db',
                                                boxShadow: state.isFocused ? '0 0 0 1px #9ca3af' : 'none',
                                                '&:hover': { borderColor: '#9ca3af' },
                                            }),
                                        }}
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm text-gray-700 mb-1">Role Description</label>
                                <textarea
                                    rows={4}
                                    className="w-full rounded-2xl border border-gray-300 px-4 py-3"
                                    placeholder="Include your key roles & responsibilities, as well as measurable achievements where possible to quantify your impact/highlight value."
                                    value={exp.description}
                                    maxLength={2000}
                                    onChange={(e) => handleExperienceChange(idx, { description: e.target.value })}
                                />
                                <div className="text-xs text-gray-500 mt-1 text-right">
                                    {exp.description?.length || 0}/2000 characters
                                </div>
                            </div>

                            {experienceCount > 1 && (
                                <div className="flex justify-end">
                                    <button
                                        type="button"
                                        className="text-sm text-red-600 flex items-center gap-2"
                                        onClick={() => removeExperienceRow(idx)}
                                    >
                                        <img src="/Trash 2.svg" alt="Delete" className="w-4 h-4" />
                                        Remove
                                    </button>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
            {section === 'education' && (
                <div>
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-2xl font-semibold">Education</h2>
                        <button type="button" className="px-4 py-2 rounded-full border-2 text-sm" onClick={addEducationRow}>
                            + Add Education
                        </button>
                    </div>
                    {educationList.map((ed, idx) => (
                        <div 
                            key={`education-${idx}`} 
                            ref={(el) => { if (el) educationItemRefs.current[idx] = el; }}
                            className="space-y-4 mb-8"
                        >
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm text-gray-700 mb-1">School</label>
                                    <input
                                        className="w-full rounded-full border border-gray-300 px-4 py-2"
                                        value={ed.school}
                                        onChange={(e) => handleEducationChange(idx, { school: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-700 mb-1">Field of Study</label>
                                    <input
                                        className="w-full rounded-full border border-gray-300 px-4 py-2"
                                        value={ed.fieldOfStudy}
                                        onChange={(e) => handleEducationChange(idx, { fieldOfStudy: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-700 mb-1">Degree</label>
                                    <input
                                        className="w-full rounded-full border border-gray-300 px-4 py-2"
                                        value={ed.degree}
                                        onChange={(e) => handleEducationChange(idx, { degree: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-700 mb-1">Grade</label>
                                    <input
                                        className="w-full rounded-full border border-gray-300 px-4 py-2"
                                        value={ed.grade}
                                        onChange={(e) => handleEducationChange(idx, { grade: e.target.value })}
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 mt-4">
                                <div>
                                    <label className="block text-sm text-gray-700 mb-1">Country</label>
                                    <Select
                                        options={[...countryList().getData()].sort((a, b) => (a.label === 'South Africa' ? -1 : b.label === 'South Africa' ? 1 : a.label.localeCompare(b.label)))}
                                        value={countryList().getData().find(c => c.value === ed.countryCode || c.label === ed.country) || null}
                                        onChange={(selectedOption) => {
                                            const code = selectedOption?.value || "";
                                            handleEducationChange(idx, {
                                                country: selectedOption?.label || "",
                                                countryCode: code,
                                                provinceState: ""
                                            });
                                        }}
                                        placeholder="Select Country"
                                        isSearchable
                                        classNamePrefix="react-select"
                                        styles={{
                                            control: (provided, state) => ({
                                                ...provided,
                                                borderRadius: '9999px',
                                                borderColor: state.isFocused ? '#9ca3af' : '#d1d5db',
                                                boxShadow: state.isFocused ? '0 0 0 1px #9ca3af' : 'none',
                                                '&:hover': { borderColor: '#9ca3af' },
                                            }),
                                        }}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-700 mb-1">Province/State</label>
                                    {ed.countryCode ? (
                                        <Select
                                            options={State.getStatesOfCountry(ed.countryCode).map((s) => ({ value: s.isoCode, label: s.name }))}
                                            value={ed.provinceState ? { value: ed.provinceState, label: ed.provinceState } : null}
                                            onChange={(opt) => handleEducationChange(idx, { provinceState: opt?.label || '' })}
                                            placeholder="Select Province/State"
                                            isSearchable
                                            classNamePrefix="react-select"
                                            styles={{
                                                control: (provided, state) => ({
                                                    ...provided,
                                                    borderRadius: '9999px',
                                                    borderColor: state.isFocused ? '#9ca3af' : '#d1d5db',
                                                    boxShadow: state.isFocused ? '0 0 0 1px #9ca3af' : 'none',
                                                    '&:hover': { borderColor: '#9ca3af' },
                                                }),
                                            }}
                                        />
                                    ) : (
                                        <input
                                            className="rounded-full border px-4 py-2 w-full text-sm bg-gray-50 border-gray-300"
                                            placeholder="Select country first"
                                            value={ed.provinceState}
                                            onChange={(e) => handleEducationChange(idx, { provinceState: e.target.value })}
                                            disabled
                                        />
                                    )}
                                </div>
                            </div>

                            <label className="inline-flex items-center gap-2 text-sm text-gray-800">
                                <input
                                    type="checkbox"
                                    checked={ed.isCurrent}
                                    onChange={(e) => {
                                        handleEducationChange(idx, { 
                                            isCurrent: e.target.checked,
                                            endDate: e.target.checked ? "" : ed.endDate
                                        });
                                    }}
                                />
                                I'm currently studying
                            </label>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                                <div>
                                    <label className="block text-sm text-gray-700 mb-1">Start Date</label>
                                    <input
                                        type="date"
                                        className="w-full rounded-full border border-gray-300 px-4 py-2"
                                        value={ed.startDate}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            // Ensure year is only 4 digits
                                            if (value) {
                                                const date = new Date(value);
                                                if (!isNaN(date.getTime())) {
                                                    const year = date.getFullYear();
                                                    // If year has more than 4 digits, restrict it
                                                    if (year > 9999 || year < 1000) {
                                                        // Extract only first 4 digits of year
                                                        const yearStr = year.toString().slice(0, 4);
                                                        const month = String(date.getMonth() + 1).padStart(2, '0');
                                                        const day = String(date.getDate()).padStart(2, '0');
                                                        const formattedDate = `${yearStr}-${month}-${day}`;
                                                        handleEducationChange(idx, { startDate: formattedDate });
                                                        return;
                                                    }
                                                }
                                            }
                                            handleEducationChange(idx, { startDate: value });
                                        }}
                                        max="9999-12-31"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-700 mb-1">End Date</label>
                                    <input
                                        type="date"
                                        disabled={ed.isCurrent}
                                        className="w-full rounded-full border border-gray-300 px-4 py-2 disabled:opacity-60"
                                        value={ed.endDate}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            // Ensure year is only 4 digits
                                            if (value) {
                                                const date = new Date(value);
                                                if (!isNaN(date.getTime())) {
                                                    const year = date.getFullYear();
                                                    // If year has more than 4 digits, restrict it
                                                    if (year > 9999 || year < 1000) {
                                                        // Extract only first 4 digits of year
                                                        const yearStr = year.toString().slice(0, 4);
                                                        const month = String(date.getMonth() + 1).padStart(2, '0');
                                                        const day = String(date.getDate()).padStart(2, '0');
                                                        const formattedDate = `${yearStr}-${month}-${day}`;
                                                        handleEducationChange(idx, { endDate: formattedDate });
                                                        return;
                                                    }
                                                }
                                            }
                                            handleEducationChange(idx, { endDate: value });
                                        }}
                                        max="9999-12-31"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm text-gray-700 mb-1">Description</label>
                                <textarea
                                    rows={4}
                                    className="w-full rounded-2xl border border-gray-300 px-4 py-3"
                                    value={ed.description}
                                    onChange={(e) => handleEducationChange(idx, { description: e.target.value })}
                                />
                            </div>

                            {educationCount > 1 && (
                                <div className="flex justify-end">
                                    <button
                                        type="button"
                                        className="text-sm text-red-600 flex items-center gap-2"
                                        onClick={() => removeEducationRow(idx)}
                                    >
                                        <img src="/Trash 2.svg" alt="Delete" className="w-4 h-4" />
                                        Remove
                                    </button>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
            {section === 'roles' && (
                <div className="space-y-6">
                    <div>
                        <label className="block text-sm text-gray-700 mb-2">What category best fits what you're looking for?</label>
                        <div className="hidden md:flex md:flex-wrap md:gap-2 md:mb-2">
                            {ROLE_CATEGORY_OPTIONS.map((label) => {
                                const cls = getCategoryClass(label);
                                const isActive = (editForm.rolesOpenTo || []).includes(label);
                                return (
                                    <button
                                        key={label}
                                        type="button"
                                        className={`category-pill ${cls} ${isActive ? 'active' : ''}`}
                                        onClick={() => {
                                            setEditForm((p) => ({
                                                ...p,
                                                rolesOpenTo: isActive
                                                    ? (p.rolesOpenTo || []).filter((c) => c !== label)
                                                    : [ ...(p.rolesOpenTo || []), label ],
                                            }));
                                        }}
                                    >
                                        {label}
                                    </button>
                                );
                            })}
                            {(editForm.rolesOpenTo || []).length > 0 && (
                                <button type="button" className="category-pill clear-all" onClick={() => setEditForm((p) => ({ ...p, rolesOpenTo: [] }))}>Clear All</button>
                            )}
                        </div>
                        <div className="md:hidden">
                            <select
                                className="w-full rounded-full border border-gray-300 px-4 py-2"
                                onChange={(e) => {
                                    const val = e.target.value;
                                    if (!val) return;
                                    setEditForm((p) => ({
                                        ...p,
                                        rolesOpenTo: (p.rolesOpenTo || []).includes(val) ? p.rolesOpenTo : [ ...(p.rolesOpenTo || []), val ],
                                    }));
                                    e.target.value = '';
                                }}
                            >
                                <option value="">Select a category…</option>
                                {ROLE_CATEGORY_OPTIONS.filter((c) => !(editForm.rolesOpenTo || []).includes(c)).map((c) => (
                                    <option key={c} value={c}>{c}</option>
                                ))}
                            </select>
                            {(editForm.rolesOpenTo || []).length > 0 && (
                                <div className="flex flex-wrap gap-2 mt-2">
                                    {(editForm.rolesOpenTo || []).map((c) => (
                                        <span key={c} className={`category-pill ${getCategoryClass(c)}`}>
                                            {c}
                                        </span>
                                    ))}
                                    <button type="button" className="category-pill clear-all" onClick={() => setEditForm((p) => ({ ...p, rolesOpenTo: [] }))}>Clear All</button>
                                </div>
                            )}
                        </div>
                    </div>

                    <div>
                        <label className="flex justify-between items-center text-sm text-gray-700 mb-2">
                            <span>What roles are you most interested in?</span>
                            <span className="text-xs text-gray-500">'Enter' to add</span>
                        </label>
                        <input
                            className="w-full rounded-full border border-gray-300 px-4 py-2"
                            value={editForm._roleInput || ''}
                            onChange={(e) => setEditForm((p) => ({ ...p, _roleInput: e.target.value }))}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    const val = (editForm._roleInput || '').trim();
                                    if (!val) return;
                                    const alreadyExists = (editForm.roleTitles || []).some((r) => r.toLowerCase() === val.toLowerCase());
                                    if (!alreadyExists) {
                                        setEditForm((p) => ({
                                            ...p,
                                            roleTitles: [ ...(p.roleTitles || []), val ],
                                            _roleInput: '',
                                        }));
                                    } else {
                                        setEditForm((p) => ({ ...p, _roleInput: '' }));
                                    }
                                }
                            }}
                        />
                        {(editForm.roleTitles || []).length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-3">
                                {(editForm.roleTitles || []).map((role) => (
                                    <span key={role} className="px-3 py-1.5 rounded-full bg-gray-100 text-sm flex items-center gap-2">
                                        {role}
                                        <button
                                            type="button"
                                            className="text-gray-500 hover:text-gray-800"
                                            onClick={() => setEditForm((p) => ({
                                                ...p,
                                                roleTitles: (p.roleTitles || []).filter((r) => r !== role),
                                            }))}
                                        >
                                            ×
                                        </button>
                                    </span>
                                ))}
                                <button type="button" className="category-pill clear-all" onClick={() => setEditForm((p) => ({ ...p, roleTitles: [] }))}>Clear All</button>
                            </div>
                        )}
                    </div>

                    <div>
                        <label className="block text-sm text-gray-700 mb-2">What type of Employment are you looking for?</label>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {EMPLOYMENT_TYPE_OPTIONS.map((opt) => {
                                const isChecked = (editForm.employmentTypes || []).includes(opt);
                                return (
                                    <label key={opt} className="flex items-center gap-2 text-sm text-gray-800">
                                        <input
                                            type="checkbox"
                                            className="rounded accent-black"
                                            checked={isChecked}
                                            onChange={() => {
                                                setEditForm((p) => ({
                                                    ...p,
                                                    employmentTypes: isChecked
                                                        ? (p.employmentTypes || []).filter((t) => t !== opt)
                                                        : [ ...(p.employmentTypes || []), opt ],
                                                }));
                                            }}
                                        />
                                        {opt}
                                    </label>
                                );
                            })}
                        </div>
                        {(editForm.employmentTypes || []).length > 0 && (
                            <button type="button" className="mt-3 category-pill clear-all" onClick={() => setEditForm((p) => ({ ...p, employmentTypes: [] }))}>
                                Clear All
                            </button>
                        )}
                    </div>
                </div>
            )}
            {section === 'certifications' && (
                <div>
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-2xl font-semibold">Certifications</h2>
                        <button type="button" className="px-4 py-2 rounded-full border-2 text-sm" onClick={addCertificationRow}>
                            + Add Certification
                        </button>
                    </div>
                    {certificationList.map((cert, idx) => (
                        <div 
                            key={`certification-${idx}`} 
                            ref={(el) => { if (el) certificationItemRefs.current[idx] = el; }}
                            className="space-y-4 mb-8"
                        >
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                                <div>
                                    <label className="block text-sm text-gray-700 mb-1">Name of course</label>
                                    <input
                                        className="w-full rounded-full border border-gray-300 px-4 py-2"
                                        value={cert.courseName}
                                        onChange={(e) => handleCertificationChange(idx, { courseName: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-700 mb-1">Issuing organisation</label>
                                    <input
                                        className="w-full rounded-full border border-gray-300 px-4 py-2"
                                        value={cert.issuingOrg}
                                        onChange={(e) => handleCertificationChange(idx, { issuingOrg: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-700 mb-1">Issue date</label>
                                    <input
                                        type="date"
                                        className="w-full rounded-full border border-gray-300 px-4 py-2"
                                        value={cert.issueDate}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            // Ensure year is only 4 digits
                                            if (value) {
                                                const date = new Date(value);
                                                if (!isNaN(date.getTime())) {
                                                    const year = date.getFullYear();
                                                    // If year has more than 4 digits, restrict it
                                                    if (year > 9999 || year < 1000) {
                                                        // Extract only first 4 digits of year
                                                        const yearStr = year.toString().slice(0, 4);
                                                        const month = String(date.getMonth() + 1).padStart(2, '0');
                                                        const day = String(date.getDate()).padStart(2, '0');
                                                        const formattedDate = `${yearStr}-${month}-${day}`;
                                                        handleCertificationChange(idx, { issueDate: formattedDate });
                                                        return;
                                                    }
                                                }
                                            }
                                            handleCertificationChange(idx, { issueDate: value });
                                        }}
                                        max="9999-12-31"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-700 mb-1">Expiration date</label>
                                    <input
                                        type="date"
                                        className="w-full rounded-full border border-gray-300 px-4 py-2"
                                        value={cert.expiryDate}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            // Ensure year is only 4 digits
                                            if (value) {
                                                const date = new Date(value);
                                                if (!isNaN(date.getTime())) {
                                                    const year = date.getFullYear();
                                                    // If year has more than 4 digits, restrict it
                                                    if (year > 9999 || year < 1000) {
                                                        // Extract only first 4 digits of year
                                                        const yearStr = year.toString().slice(0, 4);
                                                        const month = String(date.getMonth() + 1).padStart(2, '0');
                                                        const day = String(date.getDate()).padStart(2, '0');
                                                        const formattedDate = `${yearStr}-${month}-${day}`;
                                                        handleCertificationChange(idx, { expiryDate: formattedDate });
                                                        return;
                                                    }
                                                }
                                            }
                                            handleCertificationChange(idx, { expiryDate: value });
                                        }}
                                        max="9999-12-31"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-700 mb-1">Credential ID</label>
                                    <input
                                        className="w-full rounded-full border border-gray-300 px-4 py-2"
                                        value={cert.credentialId}
                                        onChange={(e) => handleCertificationChange(idx, { credentialId: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-700 mb-1">Credential URL</label>
                                    <input
                                        className="w-full rounded-full border border-gray-300 px-4 py-2"
                                        value={cert.credentialUrl}
                                        onChange={(e) => handleCertificationChange(idx, { credentialUrl: e.target.value })}
                                    />
                                </div>
                            </div>
                            {certificationCount > 1 && (
                                <div className="flex justify-end">
                                    <button
                                        type="button"
                                        className="text-sm text-red-600 flex items-center gap-2"
                                        onClick={() => removeCertificationRow(idx)}
                                    >
                                        <img src="/Trash 2.svg" alt="Delete" className="w-4 h-4" />
                                        Remove
                                    </button>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
            {section === 'languages' && (
                <div>
                    <label className="block text-sm text-gray-700 mb-1">Languages (comma-separated)</label>
                    <input className="w-full rounded-full border border-gray-300 px-4 py-2" value={(editForm.languages || []).join(', ')} onChange={(e) => {
                        const arr = e.target.value.split(',').map((s) => s.trim()).filter(Boolean);
                        setEditForm((p) => ({ ...p, languages: arr }));
                    }} />
                </div>
            )}
        </EditModal>
    );
};


export default MyProfile;
